import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';

/**
 * Eine Woche als Raster - JEDER Tag, auch der ohne Eintrag.
 *
 * Das ist der ganze Punkt. Bisher zeigte die Zeiterfassung eine flache Liste
 * der vorhandenen Zeilen; ein vergessener Dienstag war unsichtbar, weil
 * nichts da war, das ihn hätte anzeigen können.
 *
 * Ein Tag gilt als fehlend, wenn Sollzeit vorgesehen ist und nichts erfasst
 * wurde. Ein freier Freitag einer Teilzeitkraft ist nicht fehlend, ein
 * Feiertag auch nicht - beide haben keine Sollzeit.
 */

const WEEKDAY_KEYS = ['weekday.mon', 'weekday.tue', 'weekday.wed', 'weekday.thu', 'weekday.fri', 'weekday.sat', 'weekday.sun'];

function isoWeekday(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  const wd = d.getUTCDay();
  return wd === 0 ? 7 : wd;
}

/** "-2.40" statt "-2.4" - und ein Plus bekommt sein Zeichen. */
function signed(value) {
  const n = Number(value ?? 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
}

export function WeekGrid({ week, onAddDay, onRemoveEntry, readOnly = false }) {
  const { t } = useTranslation();

  if (!week) {
    return <p className="muted">{t('time.week.loading')}</p>;
  }

  return (
    <div className="week-grid">
      {week.days.map((day) => {
        const label = t(WEEKDAY_KEYS[isoWeekday(day.date) - 1]);
        const frei = day.targetHours === 0 && !day.isHoliday;
        return (
          <div
            key={day.date}
            className="week-day"
            data-missing={day.missing || undefined}
            data-off={frei || undefined}
            data-holiday={day.isHoliday || undefined}
          >
            <div className="week-day-head">
              <span className="week-day-name">{label}</span>
              <span className="muted text-xs mono">{day.date.slice(8)}.{day.date.slice(5, 7)}.</span>
            </div>

            <div className="week-day-hours num mono">{Number(day.actualHours).toFixed(2)}</div>
            <div className="muted text-xs">
              {day.isHoliday
                ? t('time.week.holiday')
                : day.targetHours > 0
                  ? t('time.week.of', { target: Number(day.targetHours).toFixed(2) })
                  : t('time.week.off')}
            </div>

            {day.missing && (
              <Badge variant="warning">{t('time.week.missing')}</Badge>
            )}

            {/* Ein Kompensationstag zählt NICHT als geleistete Zeit - sonst
                könnte ein Stundenkonto nur wachsen. Ohne diesen Hinweis
                stünde hier 0.00 neben einer Zeile über acht Stunden, und der
                Tag sähe wie ein Erfassungsfehler aus. */}
            {Number(day.compensatedHours ?? 0) > 0 && (
              <div className="muted text-xs">
                {t('time.week.compensated', {
                  hours: Number(day.compensatedHours).toFixed(2),
                })}
              </div>
            )}

            <ul className="week-day-entries">
              {day.entries.map((e) => (
                <li key={e.id}>
                  <span className="mono text-xs">
                    {e.startTime && e.endTime
                      ? `${e.startTime}-${e.endTime}${e.breakMinutes ? ` (${e.breakMinutes}′)` : ''}`
                      : `${Number(e.hours).toFixed(2)} h`}
                  </span>
                  {!readOnly && onRemoveEntry && (
                    <button
                      type="button"
                      className="week-day-remove"
                      aria-label={t('common.delete')}
                      onClick={() => onRemoveEntry(e)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>

            {!readOnly && onAddDay && day.targetHours > 0 && (
              <Button size="sm" variant="ghost" onClick={() => onAddDay(day.date)}>
                {t('time.week.add')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Soll, Ist, Saldo als drei Kacheln.
 *
 * "168 Stunden im Juli" beantwortet nicht die Frage, die gestellt wird. Erst
 * die drei nebeneinander sagen, ob das viel oder wenig war.
 */
export function BalanceTiles({ summary }) {
  const { t } = useTranslation();
  if (!summary || summary.targetHours === undefined) {
    return null;
  }
  const saldo = Number(summary.balanceHours ?? 0);
  const kompensiert = Number(summary.compensatedHours ?? 0);
  return (
    <div className="balance-tiles">
      <div className="balance-tile">
        <div className="muted text-xs">{t('time.balance.target')}</div>
        <div className="balance-value num mono">{Number(summary.targetHours).toFixed(2)}</div>
      </div>
      <div className="balance-tile">
        <div className="muted text-xs">{t('time.balance.actual')}</div>
        <div className="balance-value num mono">{Number(summary.actualHours).toFixed(2)}</div>
      </div>
      {/* Nur wenn es etwas zu erklären gibt. Eine Kachel mit 0.00, die in
          neunundneunzig von hundert Wochen 0.00 zeigt, verdrängt die drei,
          auf die es ankommt. */}
      {kompensiert > 0 && (
        <div className="balance-tile">
          <div className="muted text-xs">{t('time.balance.compensated')}</div>
          <div className="balance-value num mono">{kompensiert.toFixed(2)}</div>
        </div>
      )}
      <div className="balance-tile" data-sign={saldo > 0 ? 'plus' : saldo < 0 ? 'minus' : 'zero'}>
        <div className="muted text-xs">{t('time.balance.balance')}</div>
        <div className="balance-value num mono">{signed(saldo)}</div>
      </div>
    </div>
  );
}
